/**
 * Event Registry - Single Source of Truth
 *
 * Contains configuration for all 18 existing events.
 * DO NOT modify event names, values, or timing - ads are linked to these.
 *
 * Platform routing:
 * - ga4-client: true = fire client-side gtag() for session attribution
 * - ga4-server: true = fire GA4 Measurement Protocol (ONLY for SubmitApplication)
 * - meta-client: true = fire client-side fbq()
 * - meta-server: true = fire Meta CAPI (redundancy, deduped by event_id).
 *   Conversions only: every server event costs a Vercel + Cloud Run + CAPI
 *   round-trip, and Meta ad sets optimize on LEAD. Views/clicks use the pixel.
 */

import type { EventConfig, EventName } from "./types";

export const EventRegistry: Record<EventName, EventConfig> = {
  // ============================================
  // CONVERSION EVENTS (High value, server-side)
  // ============================================

  Lead: {
    defaults: { currency: "CHF" },
    platforms: {
      "ga4-client": true, // For session attribution
      "ga4-server": false, // Would duplicate GA4
      "meta-client": true, // Redundancy for reliability
      "meta-server": true, // Deduped by event_id, adds IP/enrichment
    },
    valueCalculation: "funnel", // Math.pow(completionRatio, 4) * 100
    requiresContext: ["userEmail", "jobLandingPageId"],
  },

  SubmitApplication: {
    defaults: { currency: "CHF" },
    platforms: {
      "ga4-client": false, // No browser available!
      "ga4-server": true, // Backend-only, uses saved attribution
      "meta-client": false, // No browser available!
      "meta-server": true, // Backend-only
    },
    valueCalculation: "matchScore", // matchScore * 10
  },

  Purchase: {
    defaults: { value: 700, currency: "CHF" },
    platforms: {
      "ga4-client": true,
      "ga4-server": false,
      "meta-client": true,
      "meta-server": true,
    },
  },

  CompleteRegistration: {
    defaults: { value: 80, currency: "CHF" },
    platforms: {
      "ga4-client": true,
      "ga4-server": false,
      "meta-client": true,
      "meta-server": true,
    },
  },

  VerifyEmail: {
    defaults: { value: 20, currency: "CHF" },
    platforms: {
      "ga4-client": true,
      "ga4-server": false,
      "meta-client": true,
      "meta-server": true,
    },
  },

  BeginCheckout: {
    defaults: {},
    platforms: {
      "ga4-client": true,
      "ga4-server": false,
      "meta-client": true,
      "meta-server": true,
    },
  },

  // ============================================
  // JOB CREATION FLOW
  // ============================================

  CreatePageButton: {
    defaults: {},
    platforms: {
      "ga4-client": true,
      "ga4-server": false,
      "meta-client": true,
      "meta-server": false, // Pixel covers it; CAPI only for conversions
    },
  },

  CreatePageSubmit: {
    defaults: { value: 50, currency: "CHF" },
    platforms: {
      "ga4-client": true,
      "ga4-server": false,
      "meta-client": true,
      "meta-server": true,
    },
  },

  ViewJobPreview: {
    defaults: {},
    platforms: {
      "ga4-client": true,
      "ga4-server": false,
      "meta-client": true,
      "meta-server": false, // Pixel covers it; CAPI only for conversions
    },
  },

  PublishButton: {
    defaults: {},
    platforms: {
      "ga4-client": true,
      "ga4-server": false,
      "meta-client": true,
      "meta-server": false, // Pixel covers it; CAPI only for conversions
    },
  },

  PublishSubmit: {
    defaults: { value: 100, currency: "CHF" },
    platforms: {
      "ga4-client": true,
      "ga4-server": false,
      "meta-client": true,
      "meta-server": true,
    },
  },

  PricingModalView: {
    defaults: {},
    platforms: {
      "ga4-client": true,
      "ga4-server": false,
      "meta-client": true,
      "meta-server": false, // Pixel covers it; CAPI only for conversions
    },
  },

  ViewJobCreationForm: {
    defaults: {},
    platforms: {
      "ga4-client": true,
      "ga4-server": false,
      "meta-client": true,
      "meta-server": false, // Pixel covers it; CAPI only for conversions
    },
  },

  // ============================================
  // APPLICANT/JOB LISTING
  // ============================================

  ViewJobListing: {
    defaults: {},
    platforms: {
      "ga4-client": true,
      "ga4-server": false,
      "meta-client": true,
      "meta-server": false, // Pixel covers it; CAPI only for conversions
    },
  },

  ApplicantSearchButton: {
    defaults: {},
    platforms: {
      "ga4-client": true,
      "ga4-server": false,
      "meta-client": true,
      "meta-server": false, // Pixel covers it; CAPI only for conversions
    },
  },

  FunnelStart: {
    defaults: {},
    platforms: {
      "ga4-client": true,
      "ga4-server": false,
      "meta-client": true,
      "meta-server": false, // Pixel covers it; CAPI only for conversions
    },
  },

  ViewForm: {
    defaults: {},
    platforms: {
      "ga4-client": true,
      "ga4-server": false,
      "meta-client": true,
      "meta-server": false, // Pixel covers it; CAPI only for conversions
    },
  },

  ViewCVUploadForm: {
    defaults: {},
    platforms: {
      "ga4-client": true,
      "ga4-server": false,
      "meta-client": true,
      "meta-server": false, // Pixel covers it; CAPI only for conversions
    },
  },

  // ============================================
  // REGISTRATION FLOW
  // ============================================

  ViewRegistrationPage: {
    defaults: {},
    platforms: {
      "ga4-client": true,
      "ga4-server": false,
      "meta-client": true,
      "meta-server": false, // Pixel covers it; CAPI only for conversions
    },
  },

  SubmitEmail: {
    defaults: {},
    platforms: {
      "ga4-client": true,
      "ga4-server": false,
      "meta-client": true,
      "meta-server": true, // Meta deduplicates via eventId
    },
  },

  ViewNeedsVerification: {
    defaults: {},
    platforms: {
      "ga4-client": true,
      "ga4-server": false,
      "meta-client": true,
      "meta-server": false, // Pixel covers it; CAPI only for conversions
    },
  },

  ViewHomepage: {
    defaults: {},
    platforms: {
      "ga4-client": true,
      "ga4-server": false,
      "meta-client": true,
      "meta-server": false, // Pixel covers it; CAPI only for conversions
    },
  },

  // ============================================
  // EXIT INTENT MODALS
  // ============================================

  ExitModalView: {
    defaults: {},
    platforms: {
      "ga4-client": true,
      "ga4-server": false,
      "meta-client": true,
      "meta-server": false, // Pixel covers it; CAPI only for conversions
    },
  },

  MarketCheckModalView: {
    defaults: {},
    platforms: {
      "ga4-client": true,
      "ga4-server": false,
      "meta-client": true,
      "meta-server": false, // Pixel covers it; CAPI only for conversions
    },
  },

  // ============================================
  // DEMO VIDEO (client-side only, no server needed)
  // ============================================

  DemoVideoPlay: {
    defaults: {},
    platforms: {
      "ga4-client": true,
      "ga4-server": false,
      "meta-client": true,
      "meta-server": false,
    },
  },

  DemoVideoProgress: {
    defaults: {},
    platforms: {
      "ga4-client": true,
      "ga4-server": false,
      "meta-client": true,
      "meta-server": false,
    },
  },

  DemoVideoComplete: {
    defaults: {},
    platforms: {
      "ga4-client": true,
      "ga4-server": false,
      "meta-client": true,
      "meta-server": false,
    },
  },

  // ============================================
  // PAGE VIEWS & OTHER
  // ============================================

  PageView: {
    defaults: {},
    platforms: {
      "ga4-client": true,
      "ga4-server": false,
      "meta-client": true,
      "meta-server": false, // Pixel covers it; CAPI only for conversions
    },
  },

  MarketCheck: {
    defaults: {},
    platforms: {
      "ga4-client": true,
      "ga4-server": false,
      "meta-client": true,
      "meta-server": false, // Pixel covers it; CAPI only for conversions
    },
  },

  AutoBuilderFormSubmit: {
    defaults: {},
    platforms: {
      "ga4-client": true,
      "ga4-server": false,
      "meta-client": true,
      "meta-server": false, // Pixel covers it; CAPI only for conversions
    },
  },
};

/**
 * Get event configuration
 */
export function getEventConfig(eventName: EventName): EventConfig {
  return EventRegistry[eventName];
}

/**
 * Check if event should go to a specific platform
 */
export function shouldFireTo(
  eventName: EventName,
  platform: keyof EventConfig["platforms"]
): boolean {
  const config = EventRegistry[eventName];
  return config?.platforms[platform] ?? false;
}

/**
 * Events that require server-side tracking (for server plugin)
 */
export const SERVER_SIDE_EVENTS: EventName[] = Object.entries(EventRegistry)
  .filter(([, config]) => config.platforms["meta-server"])
  .map(([name]) => name as EventName);
